import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Flex } from "antd"
import { MinusOutlined, PlusOutlined } from "@ant-design/icons"
import { Text } from "ui/Typography"
import { sumCheckedIn, sumGuests } from "../checkInUtils"
import styles from "./CheckInGuestsModal.module.css"

const remainingFor = (type) => Math.max((type.total ?? 0) - (type.checkedInCount ?? 0), 0)

// Check-in modal for private bookings. One stepper per guest type (Adults/Children/…), each
// picking how many *more* of that type to check in now. onConfirm returns the new absolute
// per-type counts: [{ key, checkedInCount }].
const CheckInGuestsModal = ({ open, onClose, bookingId, clientName, guestTypes = [], loading = false, onConfirm }) => {
  const [deltas, setDeltas] = useState({})

  // Reset the selection whenever the modal reopens for a (possibly different) booking.
  useEffect(() => {
    if (open) setDeltas({})
  }, [open])

  const setDelta = (key, value) => setDeltas((prev) => ({ ...prev, [key]: value }))
  const decrement = (type) => setDelta(type.key, Math.max((deltas[type.key] ?? 0) - 1, 0))
  const increment = (type) => setDelta(type.key, Math.min((deltas[type.key] ?? 0) + 1, remainingFor(type)))
  const selectAll = () => setDeltas(Object.fromEntries(guestTypes.map((type) => [type.key, remainingFor(type)])))

  const checkedIn = sumCheckedIn(guestTypes)
  const total = sumGuests(guestTypes)
  const selectedTotal = guestTypes.reduce((sum, type) => sum + (deltas[type.key] ?? 0), 0)
  const allRemaining = guestTypes.reduce((sum, type) => sum + remainingFor(type), 0)

  const handleConfirm = () =>
    onConfirm?.(guestTypes.map((type) => ({ ...type, checkedInCount: (type.checkedInCount ?? 0) + (deltas[type.key] ?? 0) })))

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={420} centered title="Check-in Guests">
      <Flex justify="space-between" align="center" className={styles.metaRow}>
        <Flex vertical gap={2}>
          <Text variant="caption" size="md" className={styles.bookingId}>BOOKING ID: {bookingId}</Text>
          <Text variant="body" size="md" fw={600} color="var(--color-text-primary)">{clientName}</Text>
        </Flex>
        <Text variant="body" size="md" color="var(--color-text-secondary-dark)">
          Checked-in: <strong>{checkedIn}/{total}</strong>
        </Text>
      </Flex>

      <Text variant="body" size="md" className={styles.selectLabel}>Select guests to check-in:</Text>

      <Flex vertical gap={8} className={styles.typeList}>
        {guestTypes.map((type) => {
          const remaining = remainingFor(type)
          const isComplete = remaining === 0
          const delta = deltas[type.key] ?? 0
          return (
            <Flex key={type.key} justify="space-between" align="center" className={styles.typeRow}>
              <Text variant="caption" size="md" className={styles.typeChip}>{type.label}</Text>
              <Text variant="body" size="md" className={isComplete ? styles.typeCountDone : styles.typeCount}>
                {type.checkedInCount}/{type.total}
              </Text>
              <Flex align="center" gap={16}>
                {!isComplete && (
                  <Flex align="center" gap={12}>
                    <Button shape="circle" size="small" icon={<MinusOutlined />} onClick={() => decrement(type)} disabled={delta <= 0} />
                    <Text variant="body" size="lg" className={styles.count}>{delta}</Text>
                    <Button shape="circle" size="small" icon={<PlusOutlined />} onClick={() => increment(type)} disabled={delta >= remaining} />
                  </Flex>
                )}
              </Flex>
            </Flex>
          )
        })}
      </Flex>

      <Flex justify="center" className={styles.selectAllWrap}>
        <button type="button" className={styles.selectAll} onClick={selectAll} disabled={allRemaining === 0}>
          Select all
        </button>
      </Flex>

      <Flex gap={8} justify="flex-end" className={styles.footer}>
        <Button shape="round" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" shape="round" onClick={handleConfirm} loading={loading} disabled={selectedTotal < 1}>
          Check-in
        </Button>
      </Flex>
    </Modal>
  )
}

CheckInGuestsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientName: PropTypes.string,
  guestTypes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      total: PropTypes.number,
      checkedInCount: PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  onConfirm: PropTypes.func
}

export default CheckInGuestsModal
